import { Column, Index } from 'typeorm';
import type { MillionVerifierSpendPurpose } from '../../constants/millionverifier';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { decimalNumber } from './decimal-number.transformer';

/**
 * PD-17610 — the MillionVerifier spend log: one row per run of the email
 * check (one Add to Campaign, one email step), with how many addresses were
 * checked, what that cost and what came back. The monthly cap and the
 * settings card's "This month" read the sum of a period's rows.
 *
 * The row is written BEFORE the checks run, holding the planned count and
 * cost, and corrected to the actual numbers afterwards. Two runs at the same
 * time therefore see each other's planned spend against the cap, and a run
 * that dies part way leaves an over-count rather than spend nobody logged.
 *
 * Each address's own result and date are on `email_address_status` and its
 * history (`email_address_status_event`, source `millionverifier`); this
 * table is the money. Not auditable: it is itself a log.
 */
@Index('IDX_millionverifier_spend_periodKey', ['periodKey'], {})
@Entity('millionverifier_spend', {}, { auditable: false })
export class MillionVerifierSpendEntity extends BaseEntity {
	/** `YYYY-MM`, the calendar month in the tenant's time zone (aiUsagePeriodKey). */
	@Column('char', { name: 'periodKey', length: 7 })
	public periodKey!: string;

	@Column('datetime', { name: 'spendDate' })
	public spendDate!: Date;

	@Column('varchar', { name: 'purpose', length: 24 })
	public purpose!: MillionVerifierSpendPurpose;

	/** The campaign the checks were for, when there was one. */
	@Column('int', { name: 'fk_campaign__id', unsigned: true, nullable: true })
	public fkCampaignId!: number | null;

	/** Tenant user (user._id) whose action ran the checks; null for a background job. */
	@Column('int', { name: 'fk_user__id', unsigned: true, nullable: true })
	public fkUserId!: number | null;

	/** Addresses checked (or, until the run finishes, planned). */
	@Column('int', { name: 'checkCount', unsigned: true, default: 0 })
	public checkCount!: number;

	/** checkCount × the cost per check at the time, in dollars. */
	@Column('decimal', { name: 'cost', precision: 12, scale: 6, default: 0, transformer: decimalNumber })
	public cost!: number;

	@Column('int', { name: 'goodCount', unsigned: true, default: 0 })
	public goodCount!: number;

	@Column('int', { name: 'riskyCount', unsigned: true, default: 0 })
	public riskyCount!: number;

	@Column('int', { name: 'badCount', unsigned: true, default: 0 })
	public badCount!: number;

	@Column('int', { name: 'unknownCount', unsigned: true, default: 0 })
	public unknownCount!: number;

	/** Calls MillionVerifier did not answer; not charged and not in checkCount. */
	@Column('int', { name: 'errorCount', unsigned: true, default: 0 })
	public errorCount!: number;

	/** Whether the run finished and the counts are actual, not planned. */
	@Column({ type: 'boolean', name: 'isComplete', width: 1, default: () => "'0'" })
	public isComplete!: boolean;
}
